// =============================================================================
// rolesHabilitations.js : les habilitations d'une fiche membre (`roles`),
// source UNIQUE des éditeurs (demande de Pierre du 14/10/2026 : « à l'avenir,
// je veux qu'on ne passe que par là », les fiches membre et la Gouvernance des
// accès).
// -----------------------------------------------------------------------------
// Avant, quatre éditeurs portaient chacun leur liste (Gouvernance des accès,
// fiche complète et édition rapide de Gestion des membres, fenêtre de
// contact) : la fiche ne savait pas poser RH, la fenêtre de contact non plus.
// « AI » (audit interne) et « CI » (contrôle interne) valent pour la mutuelle
// de la fiche ; le DG les a de droit ; le serveur les lit (auditMissions,
// controleInterne, functions/lib/rolesHabilitations.js, miroir tenu par
// rolesHabilitations.test.js). Les rôles d'administration (`role` : admin,
// superadmin) ne sont pas ici : « Comptes admin » de la Super administration.
//
// `mandat` : le rôle ouvre un mandat daté (la fiche pose ses dates).
// `historique` : rôle encore lu mais plus proposé partout (EC, plus de
// comptes d'expert-comptable depuis le 02/10/2026 : seuls les éditeurs qui le
// proposaient le gardent).
// =============================================================================

export const ROLES_HABILITATIONS = Object.freeze([
  Object.freeze({ k: "AG", l: "Délégué AG", hint: "Délégué à l'assemblée générale : mandat daté, vote en séance", mandat: "AG" }),
  Object.freeze({ k: "CA", l: "Administrateur CA", hint: "Administrateur au conseil : mandat daté, tous domaines, vote en séance", mandat: "CA" }),
  Object.freeze({ k: "CD", l: "Membre CODIR", hint: "Tous domaines, gel des avoirs" }),
  Object.freeze({ k: "DG", l: "Directeur général", hint: "Tous domaines, ressources humaines, gel des avoirs ; de droit, l'audit interne et le contrôle interne de sa mutuelle" }),
  Object.freeze({ k: "PR", l: "Président", hint: "Tous domaines" }),
  Object.freeze({ k: "RH", l: "Ressources humaines", hint: "Module Ressources humaines" }),
  Object.freeze({ k: "DPO", l: "DPO (RGPD)", hint: "Conformité : RGPD (Calypso)" }),
  Object.freeze({ k: "DPO_ADJOINT", l: "DPO adjoint", hint: "Conformité : RGPD (Calypso)" }),
  Object.freeze({ k: "AI", l: "Audit interne", hint: "Conformité : audit interne de sa mutuelle (le DG l'a de droit)" }),
  Object.freeze({ k: "CI", l: "Contrôle interne", hint: "Conformité : contrôle interne de sa mutuelle (le DG l'a de droit)" }),
  Object.freeze({ k: "EC", l: "Expert-comptable", hint: "Portail comptable restreint (Pilotage, Dépenses : pièces et exports) ; seul rôle EC = ne voit que ce portail", historique: true }),
]);

export const CODES_HABILITATIONS = Object.freeze(ROLES_HABILITATIONS.map((r) => r.k));

export const libelleHabilitation = (k) => (ROLES_HABILITATIONS.find((r) => r.k === k) || {}).l || k;

/** La fiche porte-t-elle l'habilitation `code` ? */
export const estHabilite = (fiche, code) => !!(fiche && Array.isArray(fiche.roles) && fiche.roles.includes(code));

/** Les habilitations qu'un éditeur propose : toutes, sauf les historiques
 *  qu'il ne proposait pas (`avecHistoriques`) ou celles qu'il exclut. */
export function habilitationsProposees({ avecHistoriques = false, sauf = [] } = {}) {
  return ROLES_HABILITATIONS.filter((r) => (avecHistoriques || !r.historique) && !sauf.includes(r.k));
}

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/**
 * L'accès d'une fiche à l'audit interne (« AI ») ou au contrôle interne
 * (« CI ») de sa mutuelle, tel que le serveur le décide (auditMissions,
 * controleInterne) : le super-administrateur ; sinon une personne active, ni
 * invitée ni de l'équipe d'Enio (qui ne voit que la mutuelle de
 * démonstration), DG de sa mutuelle (de droit) ou habilitée. Pour la matrice
 * de la Gouvernance des accès : jamais une décision, le serveur décide.
 */
export function accesConformite(fiche, code) {
  if (!fiche) return { ok: false, motif: null };
  if (fiche.role === "superadmin") return { ok: true, motif: "Super-administrateur" };
  if (fiche.actif === false || fiche.supprime || fiche.technique || fiche.role === "invite") return { ok: false, motif: null };
  if (norm(fiche.mutuelle) === "enio") return { ok: false, motif: null };
  if (!norm(fiche.mutuelle)) return { ok: false, motif: null };
  if (estHabilite(fiche, "DG")) return { ok: true, motif: "DG, de droit" };
  if (estHabilite(fiche, code)) return { ok: true, motif: "Habilité" };
  return { ok: false, motif: null };
}

/**
 * Fusion à trois des rôles d'une fiche (15/10/2026) : ce qui est EN BASE au
 * moment d'écrire, ce que l'éditeur a VU à l'ouverture, ce qu'il VEUT. Seuls
 * ses ajouts et ses retraits s'appliquent ; un rôle changé entre-temps par un
 * autre (le DG qui habilite AI ou CI depuis sa page, un autre administrateur)
 * reste tel qu'en base. Sans `vus`, l'éditeur ne sait pas ce qu'il a vu : ce
 * qu'il veut s'écrit tel quel, comme avant.
 */
export function fusionnerRoles(enBase, vus, voulus) {
  const base = Array.isArray(enBase) ? enBase : [];
  const v = Array.isArray(voulus) ? voulus : [];
  if (!Array.isArray(vus)) return [...v];
  const ajoutes = v.filter((r) => !vus.includes(r));
  const retires = vus.filter((r) => !v.includes(r));
  const sortie = base.filter((r) => !retires.includes(r));
  for (const r of ajoutes) if (!sortie.includes(r)) sortie.push(r);
  return sortie;
}
